/**
 * Polls a condition until it returns a non-null, non-undefined value.
 * This is useful for waiting for an async operation to complete.
 * @param condition - The condition to check
 * @param options - The polling options
 * @returns The result of the condition
 * @throws {TimeoutErrorClass} If the polling times out
 * @example await pollUntil(() => fetchUser(id), { timeoutMs: 5000 });
 */
export async function pollUntil<T>(
  condition: () => T | undefined | null,
  options: {
    timeoutMs?: number;
    intervalMs?: number;
    timeoutMessage?: string;
    TimeoutErrorClass?: new (message: string) => Error;
  } = {},
): Promise<T> {
  const {
    timeoutMs = 5000,
    intervalMs = 10,
    timeoutMessage = 'Polling timed out',
    TimeoutErrorClass = Error,
  } = options;

  const startTime = Date.now();

  while (true) {
    const result = condition();
    if (result !== null && result !== undefined) {
      return result;
    }

    if (Date.now() - startTime >= timeoutMs) {
      throw new TimeoutErrorClass(timeoutMessage);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}
